import React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  Text
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { ImportSources } from '../../../constants/importStatuses';
import { createErrorToast, createSuccessToast } from '../../../redux/modules/app';
import { hideModal } from '../../../redux/modules/modal';
import { useCreateImport } from '../../../services/import-service';

const CreateImportModal = ({ companyId, companyName }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { mutateAsync: createImport, isLoading } = useCreateImport();

  const useFormMethods = useForm({
    defaultValues: {
      name: '',
      source: ImportSources.local,
      source_path: '',
      force_ai_extraction: false,
      rows_per_minute: ''
    }
  });

  const { handleSubmit, register, watch, formState: { errors } } = useFormMethods;
  const source = watch('source');

  const onSubmit = async data => {
    try {
      const created = await createImport({
        companyId,
        name: data.name || null,
        source: data.source,
        source_path: data.source === ImportSources.dropbox ? data.source_path : null,
        force_ai_extraction: Boolean(data.force_ai_extraction),
        rows_per_minute: data.rows_per_minute ? Number(data.rows_per_minute) : null
      });

      dispatch(createSuccessToast({ title: 'Import created' }));
      dispatch(hideModal());
      history.push(`/pinsadmin/imports/${created.id}`);
    } catch (error) {
      dispatch(createErrorToast({
        title: 'The import could not be created',
        description: error?.message,
        error
      }));
    }
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Text mb={4} textStyle="bodyLg">{`New import for ${companyName}`}</Text>

        <FormControl mb={4}>
          <FormLabel>Name</FormLabel>
          <Input placeholder="Defaults to Import <today>" {...register('name')} />
        </FormControl>

        <FormControl mb={4}>
          <FormLabel>Files come from</FormLabel>
          <Select data-testid="import-source" {...register('source')}>
            <option value={ImportSources.local}>Browser upload</option>
            <option value={ImportSources.dropbox}>Dropbox folder</option>
          </Select>
        </FormControl>

        {source === ImportSources.dropbox && (
          <FormControl isInvalid={Boolean(errors.source_path)} mb={4}>
            <FormLabel>Dropbox folder</FormLabel>
            <Input placeholder="Paste the folder's shared link, or its path like /Clients/Acme Onboarding" {...register('source_path', { required: 'A Dropbox folder link or path is required' })} />
            <FormHelperText>
              {errors.source_path?.message ?? 'A shared link to the folder, or its path in the support Dropbox. A manifest.csv at its root is used when present.'}
            </FormHelperText>
          </FormControl>
        )}

        <FormControl mb={4}>
          <FormLabel>Rows per minute</FormLabel>
          <Input placeholder="Unlimited" type="number" {...register('rows_per_minute', { min: 1, max: 600 })} />
          <FormHelperText>Throttle the run; leave blank for full speed.</FormHelperText>
        </FormControl>

        <FormControl mb={6}>
          <Checkbox {...register('force_ai_extraction')}>Force AI contract extraction</Checkbox>
          <FormHelperText>
            Extracts requirements from contracts even when the company lacks the AI contract feature. Audited: who and when.
          </FormHelperText>
        </FormControl>

        <HStack justifyContent="center" spacing={4}>
          <Button variant="secondary" onClick={() => dispatch(hideModal())}>Cancel</Button>
          <Button isLoading={isLoading} type="submit">Create</Button>
        </HStack>
      </form>
    </Box>
  );
};

CreateImportModal.propTypes = {
  companyId: PropTypes.number.isRequired,
  companyName: PropTypes.string.isRequired
};

export default CreateImportModal;
